import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import './PublicCanteenStatistics.css'

interface Comment {
  comment: string
  food_quality_rating: number
  service_quality_rating: number
  vote_date: string
}

interface StatisticsItem {
  menu_id: string
  meal_name: string
  meal_type: string
  menu_number?: number
  meal_date: string
  total_food_votes: number
  average_food_rating: number
  total_service_votes: number
  average_service_rating: number
  food_rating_1_count: number
  food_rating_2_count: number
  food_rating_3_count: number
  food_rating_4_count: number
  food_rating_5_count: number
  service_rating_1_count: number
  service_rating_2_count: number
  service_rating_3_count: number
  service_rating_4_count: number
  service_rating_5_count: number
}

interface StatisticsData {
  school_year: string
  summary: {
    total_meals_voted: number
    total_food_votes_count: number
    total_service_votes_count: number
    overall_food_rating: number
    overall_service_rating: number
  }
  statistics: StatisticsItem[]
}

interface DailyData {
  date: string
  food_rating: number
  service_rating: number
  meals_count: number
}

/** Lokální YYYY-MM-DD bez UTC posunu (toISOString v CZ posouvá den). */
function formatLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function currentSchoolYear(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  return month >= 9 ? `${year}-${year + 1}` : `${year - 1}-${year}`
}

function periodDateRange(
  periodFilter: string,
  dateFrom: string,
  dateTo: string
): { from: string; to: string } | null {
  if (periodFilter === 'month') {
    const now = new Date()
    return {
      from: formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 1)),
      to: formatLocalDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    }
  }
  if (periodFilter === 'week') {
    const now = new Date()
    const dayOfWeek = now.getDay()
    const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)
    const monday = new Date(now.getFullYear(), now.getMonth(), diff)
    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)
    return { from: formatLocalDate(monday), to: formatLocalDate(sunday) }
  }
  if (periodFilter === 'custom') {
    if (!dateFrom && !dateTo) return null
    return { from: dateFrom || '1970-01-01', to: dateTo || '2999-12-31' }
  }
  // 'all' — bez omezení data (celá načtená historie)
  return null
}

export default function PublicCanteenStatistics() {
  const navigate = useNavigate()
  const [data, setData] = useState<StatisticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Фильтры
  const [searchQuery, setSearchQuery] = useState('')
  const [filterMealType, setFilterMealType] = useState<string>('all')
  const [minRating, setMinRating] = useState<number>(0)
  const [selectedMeal, setSelectedMeal] = useState<StatisticsItem | null>(null)
  const [comments, setComments] = useState<Comment[]>([])
  const [loadingComments, setLoadingComments] = useState(false)
  
  // Filtr období — výchozí celá historie (meal_date = den jídla)
  const [periodFilter, setPeriodFilter] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')

  const loadComments = async (menuId: string) => {
    setLoadingComments(true)
    try {
      const apiUrl = (import.meta as any).env?.VITE_API_URL || window.location.origin + '/api'
      const response = await fetch(`${apiUrl}/public-canteen/comments?menu_id=${menuId}`)
      if (response.ok) {
        const result = await response.json()
        setComments(result.comments || [])
      }
    } catch (err) {
      console.error('Error loading comments:', err)
      setComments([])
    } finally {
      setLoadingComments(false)
    }
  }

  const openMealDetail = (meal: StatisticsItem) => {
    setSelectedMeal(meal)
    loadComments(meal.menu_id)
  }

  const loadStatistics = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const apiUrl = (import.meta as any).env?.VITE_API_URL || window.location.origin + '/api'
      // Celá historie hlasů jednou; období filtruje klient (meal_date = den jídla)
      const response = await fetch(`${apiUrl}/public-canteen/statistics`)
      
      if (!response.ok) {
        throw new Error('Nepodařilo se načíst statistiky')
      }
      
      const result: StatisticsData = await response.json()
      if (!result.school_year) {
        result.school_year = currentSchoolYear()
      }
      setData(result)
    } catch (err: any) {
      setError(err.message || 'Nepodařilo se načíst statistiky')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStatistics()
  }, [])

  const renderStars = (rating: number | string | null | undefined, showNumber = true) => {
    const r = Number(rating)
    if (!r || isNaN(r)) return null
    
    const fullStars = Math.floor(r)
    const hasHalfStar = r % 1 >= 0.5
    
    return (
      <div className="stars-display">
        {[...Array(fullStars)].map((_, i) => <span key={`f-${i}`} className="star-full">⭐</span>)}
        {hasHalfStar && <span className="star-half">✨</span>}
        {showNumber && <span className="rating-value">{r.toFixed(1)}</span>}
      </div>
    )
  }

  const getMealTypeName = (type: string): string => {
    const names: Record<string, string> = {
      soup: 'Polévka',
      breakfast: 'Snídaně',
      lunch: 'Oběd',
      dinner: 'Večeře',
      snack: 'Svačina'
    }
    return names[type] || type
  }

  const getRatingColor = (rating: number): string => {
    if (rating >= 4.5) return '#10b981' // Zelená
    if (rating >= 3.5) return '#3b82f6' // Modrá
    if (rating >= 2.5) return '#f59e0b' // Žlutá
    return '#ef4444' // Červená
  }

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00')
    const days = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota']
    return `${days[date.getDay()]}, ${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`
  }

  // Группировка по датам
  const groupByDate = (items: StatisticsItem[]) => {
    const grouped: Record<string, StatisticsItem[]> = {}
    items.forEach(item => {
      if (!grouped[item.meal_date]) {
        grouped[item.meal_date] = []
      }
      grouped[item.meal_date].push(item)
    })
    return grouped
  }

  if (loading) {
    return (
      <div className="public-statistics-maps">
        <div className="stats-container">
          <div className="loading">Načítání statistik...</div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="public-statistics-maps">
        <div className="stats-container">
          <div className="error-message">❌ {error || 'Chyba načítání'}</div>
        </div>
      </div>
    )
  }

  // Фильтрация (вычисляется только после проверки data)
  const range = periodDateRange(periodFilter, dateFrom, dateTo)
  const filteredStatistics = data.statistics
    .filter(item => {
      if (range) {
        if (range.from && item.meal_date < range.from) return false
        if (range.to && item.meal_date > range.to) return false
      }
      // Поиск по названию
      if (searchQuery && !item.meal_name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false
      }
      // Фильтр по типу и номеру обеда
      if (filterMealType !== 'all') {
        if (filterMealType === 'lunch1') {
          if (item.meal_type !== 'lunch' || item.menu_number !== 1) {
            return false
          }
        } else if (filterMealType === 'lunch2') {
          if (item.meal_type !== 'lunch' || item.menu_number !== 2) {
            return false
          }
        } else if (item.meal_type !== filterMealType) {
          return false
        }
      }
      // Минимальный рейтинг (округление вниз - если 3.9, то попадает в категорию 3)
      if (minRating > 0) {
        const roundedRating = Math.floor(Number(item.average_food_rating) || 0)
        if (roundedRating !== minRating) {
          return false
        }
      }
      return true
    })
    .sort((a, b) => {
      // Сначала по рейтингу (если выбран фильтр по рейтингу)
      if (minRating > 0) {
        const ratingDiff = (Number(b.average_food_rating) || 0) - (Number(a.average_food_rating) || 0)
        if (Math.abs(ratingDiff) > 0.01) {
          return ratingDiff
        }
      }
      // Затем по дате (новые сначала)
      const dateDiff = new Date(b.meal_date).getTime() - new Date(a.meal_date).getTime()
      if (dateDiff !== 0) {
        return dateDiff
      }
      // Затем по типу еды
      const typeOrder: Record<string, number> = { breakfast: 1, lunch: 2, dinner: 3, snack: 4, soup: 5 }
      const typeDiff = (typeOrder[a.meal_type] || 99) - (typeOrder[b.meal_type] || 99)
      if (typeDiff !== 0) {
        return typeDiff
      }
      // Затем по номеру меню
      return (a.menu_number || 0) - (b.menu_number || 0)
    })

  // Подготовка данных для графика
  const prepareChartData = (statistics: StatisticsItem[]): DailyData[] => {
    const dailyStats: Record<string, { food: number[], service: number[], count: number }> = {}
    
    // Используем отфильтрованные данные для графика
    statistics.forEach(item => {
      if (!dailyStats[item.meal_date]) {
        dailyStats[item.meal_date] = { food: [], service: [], count: 0 }
      }
      // Добавляем только валидные значения (больше 0)
      if (item.average_food_rating && item.average_food_rating > 0) {
        dailyStats[item.meal_date].food.push(Number(item.average_food_rating))
      }
      if (item.average_service_rating && item.average_service_rating > 0) {
        dailyStats[item.meal_date].service.push(Number(item.average_service_rating))
      }
      dailyStats[item.meal_date].count++
    })
    
    return Object.keys(dailyStats)
      .sort()
      .map(date => {
        const stats = dailyStats[date]
        const avgFood = stats.food.length > 0 
          ? stats.food.reduce((a, b) => a + b, 0) / stats.food.length 
          : null
        const avgService = stats.service.length > 0 
          ? stats.service.reduce((a, b) => a + b, 0) / stats.service.length 
          : null
        
        return {
          date,
          food_rating: avgFood || 0,
          service_rating: avgService || 0,
          meals_count: stats.count
        }
      })
      .filter(d => d.food_rating > 0 || d.service_rating > 0) // Показывать только даты с данными
  }

  const groupedByDate = groupByDate(filteredStatistics)
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
  const chartData = prepareChartData(filteredStatistics)

  // Celkové hodnocení podle aktivních filtrů (vážený průměr z viditelných jídel)
  const filteredSummary = (() => {
    let foodVotes = 0
    let serviceVotes = 0
    let foodSum = 0
    let serviceSum = 0
    for (const item of filteredStatistics) {
      const fv = Number(item.total_food_votes) || 0
      const sv = Number(item.total_service_votes) || 0
      const fr = Number(item.average_food_rating) || 0
      const sr = Number(item.average_service_rating) || 0
      if (fv > 0 && fr > 0) {
        foodVotes += fv
        foodSum += fr * fv
      }
      if (sv > 0 && sr > 0) {
        serviceVotes += sv
        serviceSum += sr * sv
      }
    }
    return {
      total_meals_voted: filteredStatistics.length,
      total_food_votes_count: foodVotes,
      total_service_votes_count: serviceVotes,
      overall_food_rating: foodVotes > 0 ? foodSum / foodVotes : 0,
      overall_service_rating: serviceVotes > 0 ? serviceSum / serviceVotes : 0,
    }
  })()

  return (
    <div className="public-statistics-maps">
      <div className="stats-container">
        <div className="stats-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Zpět
        </button>
        <h1>📊 Hodnocení jídel</h1>
        <p className="subtitle">
          Školní rok {data.school_year}
          {periodFilter === 'month' && ' · tento měsíc'}
          {periodFilter === 'week' && ' · tento týden'}
          {periodFilter === 'custom' && dateFrom && dateTo && ` · ${dateFrom} – ${dateTo}`}
          <span style={{ display: 'block', fontSize: '0.85rem', opacity: 0.85, marginTop: '0.25rem' }}>
            Podle data jídla (kiosk = včerejší, aplikace = dnešní)
          </span>
        </p>
        <button 
          className="kiosk-button" 
          onClick={() => navigate('/public-canteen/kiosk')}
          title="Otevřít pro dotykovou obrazovku"
        >
          🖥️ Dotyková obrazovka
        </button>
      </div>

      {/* Глобальная статистика — podle filtrů */}
      <div className="global-stats-card">
        <h2>📈 Celkové hodnocení</h2>
        <div className="global-stats-grid">
          <div className="global-stat-item">
            <div className="global-stat-value">
              {filteredSummary.overall_food_rating > 0
                ? filteredSummary.overall_food_rating.toFixed(1)
                : '0.0'}
            </div>
            <div className="global-stat-label">🍴 Kvalita jídla</div>
            <div className="global-stat-count">{filteredSummary.total_food_votes_count} hodnocení</div>
          </div>
          <div className="global-stat-item">
            <div className="global-stat-value">
              {filteredSummary.overall_service_rating > 0
                ? filteredSummary.overall_service_rating.toFixed(1)
                : '0.0'}
            </div>
            <div className="global-stat-label">👥 Kvalita obsluhy</div>
            <div className="global-stat-count">{filteredSummary.total_service_votes_count} hodnocení</div>
          </div>
          <div className="global-stat-item">
            <div className="global-stat-value">{filteredSummary.total_meals_voted}</div>
            <div className="global-stat-label">🍽️ Ohodnocených jídel</div>
            <div className="global-stat-count">Podle filtrů</div>
          </div>
        </div>
      </div>

      {/* Поиск и фильтры */}
      <div className="search-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Hledat jídlo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filters-row">
          <select 
            value={filterMealType} 
            onChange={(e) => setFilterMealType(e.target.value)}
            className="filter-select"
          >
            <option value="all">Všechny typy</option>
            <option value="breakfast">Snídaně</option>
            <option value="lunch1">Oběd - 1. chod</option>
            <option value="lunch2">Oběd - 2. chod</option>
            <option value="dinner">Večeře</option>
          </select>
          
          <select 
            value={minRating} 
            onChange={(e) => setMinRating(Number(e.target.value))}
            className="filter-select"
          >
            <option value="0">Všechna hodnocení</option>
            <option value="5">5</option>
            <option value="4">4</option>
            <option value="3">3</option>
            <option value="2">2</option>
            <option value="1">1</option>
          </select>
          
          <select 
            value={periodFilter} 
            onChange={(e) => {
              setPeriodFilter(e.target.value)
              if (e.target.value !== 'custom') {
                setDateFrom('')
                setDateTo('')
              }
            }}
            className="filter-select"
          >
            <option value="all">Všechny období</option>
            <option value="month">Tento měsíc</option>
            <option value="week">Tento týden</option>
            <option value="custom">Vlastní období</option>
          </select>
          
          {periodFilter === 'custom' && (
            <>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="filter-date"
                placeholder="Od"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="filter-date"
                placeholder="Do"
              />
            </>
          )}
          
          <button 
            className="reset-button"
            onClick={() => {
              setSearchQuery('')
              setFilterMealType('all')
              setMinRating(0)
              setPeriodFilter('all')
              setDateFrom('')
              setDateTo('')
            }}
          >
            🔄 Resetovat
          </button>
        </div>
      </div>

      {/* График динамики */}
      {chartData.length > 0 && (
        <div className="chart-card">
          <h2>📊 Dynamika hodnocení</h2>
          <div className="chart-container">
            <div className="chart-legend">
              <div className="legend-item">
                <span className="legend-color food"></span>
                <span>Kvalita jídla</span>
              </div>
              <div className="legend-item">
                <span className="legend-color service"></span>
                <span>Kvalita obsluhy</span>
              </div>
            </div>
            <div className="chart-wrapper">
              <svg className="chart-svg" viewBox={`0 0 ${Math.max(800, chartData.length * 40)} 300`}>
                {/* Grid lines */}
                {[1, 2, 3, 4, 5].map(level => (
                  <line
                    key={level}
                    x1="50"
                    y1={250 - (level - 1) * 50}
                    x2={Math.max(800, chartData.length * 40) - 50}
                    y2={250 - (level - 1) * 50}
                    stroke="#e5e7eb"
                    strokeWidth="1"
                    strokeDasharray="4,4"
                  />
                ))}
                
                {/* Food rating line - только если есть данные */}
                {chartData.some(d => d.food_rating > 0) && (
                  <polyline
                    points={chartData
                      .map((d, i) => {
                        if (d.food_rating > 0) {
                          return `${50 + i * 40},${250 - ((d.food_rating - 1) * 50)}`
                        }
                        return null
                      })
                      .filter(p => p !== null)
                      .join(' ')}
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="3"
                    className="chart-line food-line"
                  />
                )}
                
                {/* Service rating line - только если есть данные */}
                {chartData.some(d => d.service_rating > 0) && (
                  <polyline
                    points={chartData
                      .map((d, i) => {
                        if (d.service_rating > 0) {
                          return `${50 + i * 40},${250 - ((d.service_rating - 1) * 50)}`
                        }
                        return null
                      })
                      .filter(p => p !== null)
                      .join(' ')}
                    fill="none"
                    stroke="#3b82f6"
                    strokeWidth="3"
                    className="chart-line service-line"
                  />
                )}
                
                {/* Points - только для валидных данных */}
                {chartData.map((d, i) => (
                  <g key={i}>
                    {d.food_rating > 0 && (
                      <circle
                        cx={50 + i * 40}
                        cy={250 - ((d.food_rating - 1) * 50)}
                        r="5"
                        fill="#10b981"
                        stroke="white"
                        strokeWidth="2"
                      />
                    )}
                    {d.service_rating > 0 && (
                      <circle
                        cx={50 + i * 40}
                        cy={250 - ((d.service_rating - 1) * 50)}
                        r="5"
                        fill="#3b82f6"
                        stroke="white"
                        strokeWidth="2"
                      />
                    )}
                  </g>
                ))}
                
                {/* Y-axis labels */}
                {[1, 2, 3, 4, 5].map(level => (
                  <text
                    key={level}
                    x="45"
                    y={255 - (level - 1) * 50}
                    textAnchor="end"
                    fontSize="12"
                    fill="#6b7280"
                  >
                    {level}
                  </text>
                ))}
                
                {/* X-axis labels (dates) */}
                {chartData.map((d, i) => {
                  if (i % Math.ceil(chartData.length / 10) === 0 || i === chartData.length - 1) {
                    const date = new Date(d.date + 'T00:00:00')
                    return (
                      <text
                        key={i}
                        x={50 + i * 40}
                        y="280"
                        textAnchor="middle"
                        fontSize="10"
                        fill="#6b7280"
                        transform={`rotate(-45 ${50 + i * 40} 280)`}
                      >
                        {date.getDate()}.{date.getMonth() + 1}
                      </text>
                    )
                  }
                  return null
                })}
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Результаты */}
      <div className="results-info">
        Zobrazeno {filteredStatistics.length} z {data.statistics.length} jídel
      </div>

      {filteredStatistics.length === 0 ? (
        <div className="no-results">
          <p>Nenalezeny žádné výsledky</p>
        </div>
      ) : (
        <div className="meals-by-dates">
          {sortedDates.map(date => {
            const dateItems = groupedByDate[date]
            // Правильный расчет средних значений
            const foodRatings = dateItems.filter(item => item.average_food_rating && !isNaN(item.average_food_rating)).map(item => Number(item.average_food_rating))
            const serviceRatings = dateItems.filter(item => item.average_service_rating && !isNaN(item.average_service_rating)).map(item => Number(item.average_service_rating))
            
            const dateAvgFood = foodRatings.length > 0 
              ? foodRatings.reduce((sum, rating) => sum + rating, 0) / foodRatings.length 
              : 0
            const dateAvgService = serviceRatings.length > 0 
              ? serviceRatings.reduce((sum, rating) => sum + rating, 0) / serviceRatings.length 
              : 0
            
            return (
              <div key={date} className="date-section-stats">
                <div className="date-header-stats">
                  <h3>{formatDate(date)}</h3>
                  <div className="date-ratings-summary">
                    {dateAvgFood > 0 && (
                      <span className="date-rating-item">
                        🍴 {dateAvgFood.toFixed(1)} ⭐
                      </span>
                    )}
                    {dateAvgService > 0 && (
                      <span className="date-rating-item">
                        👥 {dateAvgService.toFixed(1)} ⭐
                      </span>
                    )}
                    <span className="date-meals-count">
                      {dateItems.length} {dateItems.length === 1 ? 'jídlo' : 'jídel'}
                    </span>
                  </div>
                </div>
                
                <div className="meals-grid">
                  {dateItems.map((item) => (
                    <div 
                      key={item.menu_id} 
                      className="meal-card-maps"
                      onClick={() => openMealDetail(item)}
                    >
                      <div className="meal-card-header-maps">
                        <h3>{item.meal_name}</h3>
                        <span className="meal-type-badge-small">
                          {getMealTypeName(item.meal_type)}
                          {item.meal_type === 'lunch' && item.menu_number && ` - ${item.menu_number}. chod`}
                        </span>
                      </div>
                      
                      <div className="rating-row-maps">
                        <div className="rating-item">
                          <span className="rating-label-small">🍴 Jídlo:</span>
                          <div className="rating-display">
                            {item.average_food_rating ? (
                              <>
                                {renderStars(item.average_food_rating)}
                                <span className="votes-count-small">({item.total_food_votes})</span>
                              </>
                            ) : (
                              <span className="no-rating-small">Bez hodnocení</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="rating-item">
                          <span className="rating-label-small">👥 Obsluha:</span>
                          <div className="rating-display">
                            {item.average_service_rating ? (
                              <>
                                {renderStars(item.average_service_rating)}
                                <span className="votes-count-small">({item.total_service_votes})</span>
                              </>
                            ) : (
                              <span className="no-rating-small">Bez hodnocení</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
      </div>

      {/* Detail modal */}
      {selectedMeal && (
        <div className="detail-overlay" onClick={() => setSelectedMeal(null)}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="detail-header">
              <h2>{selectedMeal.meal_name}</h2>
              <button className="close-btn" onClick={() => setSelectedMeal(null)}>✕</button>
            </div>
            
            <div className="detail-body">
              <div className="detail-meta">
                <span className="meal-type-badge">
                  {getMealTypeName(selectedMeal.meal_type)}
                  {selectedMeal.meal_type === 'lunch' && selectedMeal.menu_number && ` - ${selectedMeal.menu_number}. chod`}
                </span>
                <span className="meal-date">
                  📅 {new Date(selectedMeal.meal_date + 'T00:00:00').toLocaleDateString('cs-CZ')}
                </span>
              </div>
              
              <div className="detail-ratings">
                <div className="rating-section-detail">
                  <h3>🍴 Kvalita jídla</h3>
                  <div className="rating-big">
                    {renderStars(selectedMeal.average_food_rating, false)}
                    <span className="rating-number-big">
                      {selectedMeal.average_food_rating && 
                       typeof selectedMeal.average_food_rating === 'number' && 
                       selectedMeal.average_food_rating > 0
                        ? Number(selectedMeal.average_food_rating).toFixed(1)
                        : (selectedMeal.total_food_votes > 0 ? '0.0' : '-')}
                    </span>
                  </div>
                  <p className="votes-text">{selectedMeal.total_food_votes || 0} hodnocení</p>
                  
                  <div className="rating-distribution">
                    {[5, 4, 3, 2, 1].map(star => {
                      const count = Number((selectedMeal as any)[`food_rating_${star}_count`]) || 0
                      const totalVotes = Number(selectedMeal.total_food_votes) || 0
                      const percentage = totalVotes > 0 
                        ? (count / totalVotes) * 100 
                        : 0
                      
                      return (
                        <div key={star} className="distribution-row">
                          <span className="star-label">{star} ⭐</span>
                          <div className="bar-container">
                            <div 
                              className="bar-fill" 
                              style={{ 
                                width: `${percentage}%`,
                                background: getRatingColor(star)
                              }}
                            />
                          </div>
                          <span className="count-label">{count}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
                
                <div className="rating-section-detail">
                  <h3>👥 Kvalita obsluhy</h3>
                  <div className="rating-big">
                    {renderStars(selectedMeal.average_service_rating, false)}
                    <span className="rating-number-big">
                      {selectedMeal.average_service_rating && 
                       typeof selectedMeal.average_service_rating === 'number' && 
                       selectedMeal.average_service_rating > 0
                        ? Number(selectedMeal.average_service_rating).toFixed(1)
                        : (selectedMeal.total_service_votes > 0 ? '0.0' : '-')}
                    </span>
                  </div>
                  <p className="votes-text">{selectedMeal.total_service_votes || 0} hodnocení</p>
                  
                  <div className="rating-distribution">
                    {[5, 4, 3, 2, 1].map(star => {
                      const count = Number((selectedMeal as any)[`service_rating_${star}_count`]) || 0
                      const totalVotes = Number(selectedMeal.total_service_votes) || 0
                      const percentage = totalVotes > 0 
                        ? (count / totalVotes) * 100 
                        : 0
                      
                      return (
                        <div key={star} className="distribution-row">
                          <span className="star-label">{star} ⭐</span>
                          <div className="bar-container">
                            <div 
                              className="bar-fill" 
                              style={{ 
                                width: `${percentage}%`,
                                background: getRatingColor(star)
                              }}
                            />
                          </div>
                          <span className="count-label">{count}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
              
              {comments.length > 0 && (
                <div className="comments-section">
                  <h3>💬 Hodnocení uživatelů</h3>
                  <div className="comments-list">
                    {comments.map((comment, idx) => (
                      <div key={idx} className="comment-card">
                        <div className="comment-ratings">
                          <span className="comment-rating">🍴 {comment.food_quality_rating}⭐</span>
                          <span className="comment-rating">👥 {comment.service_quality_rating}⭐</span>
                          <span className="comment-date">{comment.vote_date}</span>
                        </div>
                        <p className="comment-text">{comment.comment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {loadingComments && (
                <div className="loading-comments">Načítání komentářů...</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
